<template>
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard")
            }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.companies' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.companies")
            }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2"><span
                class="tw-text-sm">{{
                    $t("common.create-new-company") }}</span></button>
    </div>
    <div class="tw-min-h-screen tw-bg-gray-50 tw-p-4">
        <!-- Breadcrumb -->

        <!-- Form Container -->
        <div class="tw-max-w-4xl tw-mx-auto">
            <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-border-gray-200 tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-p-6 tw-border-b tw-border-gray-200 tw-bg-white">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-xl" />
                        </div>
                        <div>
                            <h1 class="tw-text-xl tw-font-semibold tw-text-gray-900">{{ $t("common.create-new-company") }}</h1>
                            <p class="tw-text-sm tw-text-gray-600">{{ $t("forms.company.create-description", "Create a new company profile with all necessary information") }}</p>
                        </div>
                    </div>
                </div>

                <!-- Form -->
                <form @submit.prevent="handleSubmit" class="tw-p-6">
                    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                        <!-- Company Name -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.name"
                                type="text"
                                :label="$t('fields.name')"
                                :placeholder="$t('forms.company.name-placeholder', 'Enter company name')"
                                left-icon="solar:buildings-2-bold-duotone"
                                required
                                :error="errors.name"
                            />
                        </div>

                        <!-- Primary Email -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.primary_email"
                                type="email"
                                :label="$t('forms.company.primary-email', 'Primary Email')"
                                :placeholder="$t('forms.company.primary-email-placeholder', 'Enter primary email')"
                                left-icon="solar:letter-bold-duotone"
                                required
                                :error="errors.primary_email"
                            />
                        </div>

                        <!-- Secondary Email -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.secondary_email"
                                type="email"
                                :label="$t('forms.company.secondary-email', 'Secondary Email')"
                                :placeholder="$t('forms.company.secondary-email-placeholder', 'Enter secondary email (optional)')"
                                left-icon="solar:letter-bold-duotone"
                                :error="errors.secondary_email"
                            />
                        </div>

                        <!-- Website -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.website"
                                type="url"
                                :label="$t('forms.company.website', 'Website')"
                                :placeholder="$t('forms.company.website-placeholder', 'Enter website URL')"
                                left-icon="solar:earth-bold-duotone"
                                :error="errors.website"
                            />
                        </div>

                        <!-- Streaming Platform -->
                        <div>
                            <FormField
                                v-model="form.streaming_platform"
                                type="select"
                                :label="$t('forms.company.streaming-platform', 'Streaming Platform')"
                                :placeholder="$t('forms.company.select-platform', 'Select streaming platform')"
                                left-icon="solar:play-circle-bold-duotone"
                                :options="streamingPlatforms"
                                :error="errors.streaming_platform"
                            />
                        </div>

                        <!-- Country -->
                        <div>
                            <FormVueSelect
                                v-model="form.country_id"
                                :options="countriesOptions"
                                option-value="id"
                                option-label="name"
                                :label="$t('forms.company.country', 'Country')"
                                :placeholder="$t('forms.company.select-country', 'Select country')"
                                left-icon="solar:map-point-bold-duotone"
                                :error="errors.country_id"
                                searchable
                                clearable
                            />
                        </div>

                        <!-- Primary Phone -->
                        <div>
                            <FormField
                                v-model="form.primary_phone"
                                type="tel"
                                :label="$t('forms.company.primary-phone', 'Primary Phone')"
                                :placeholder="$t('forms.company.primary-phone-placeholder', 'Enter primary phone')"
                                left-icon="solar:phone-bold-duotone"
                                :error="errors.primary_phone"
                            />
                        </div>

                        <!-- Secondary Phone -->
                        <div>
                            <FormField
                                v-model="form.secondary_phone"
                                type="tel"
                                :label="$t('forms.company.secondary-phone', 'Secondary Phone')"
                                :placeholder="$t('forms.company.secondary-phone-placeholder', 'Enter secondary phone (optional)')"
                                left-icon="solar:phone-bold-duotone"
                                :error="errors.secondary_phone"
                            />
                        </div>

                        <!-- Logo Upload -->
                        <div>
                            <FormFileUpload
                                v-model="form.logo"
                                :label="$t('forms.company.logo', 'Company Logo')"
                                :placeholder="$t('forms.company.logo-placeholder', 'Upload company logo')"
                                :help="$t('forms.company.logo-help', 'Recommended: 200x200px, PNG/JPG')"
                                left-icon="solar:gallery-bold-duotone"
                                accept="image/*"
                                :error="errors.logo"
                            />
                        </div>

                        <!-- Background Image Upload -->
                        <div>
                            <FormFileUpload
                                v-model="form.background_image"
                                :label="$t('forms.company.background-image', 'Background Image')"
                                :placeholder="$t('forms.company.background-placeholder', 'Upload background image')"
                                :help="$t('forms.company.background-help', 'Recommended: 1920x1080px, PNG/JPG')"
                                left-icon="solar:image-bold-duotone"
                                accept="image/*"
                                :error="errors.background_image"
                            />
                        </div>

                        <!-- Address -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.address"
                                type="text"
                                :label="$t('fields.customer_address')"
                                :placeholder="$t('forms.company.address-placeholder', 'Enter company address')"
                                left-icon="solar:map-point-bold-duotone"
                                :error="errors.address"
                            />
                        </div>

                        <!-- Description -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.description"
                                type="textarea"
                                :label="$t('forms.company.description', 'Description')"
                                :placeholder="$t('forms.company.description-placeholder', 'Enter company description')"
                                :error="errors.description"
                                rows="4"
                            />
                        </div>

                        <!-- Users -->
                        <div class="lg:tw-col-span-2">
                            <!-- Skeleton Loader for Users -->
                            <div v-if="usersLoading" class="tw-space-y-3">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    {{ $t('forms.company.users', 'Users') }}
                                </label>
                                <div class="tw-relative">
                                    <!-- Skeleton Button -->
                                    <div class="tw-w-full tw-h-[40px] tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-flex tw-items-center tw-justify-between tw-px-4">
                                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                                            <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-h-4 tw-bg-gray-300 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                        </div>
                                        <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                                <p class="tw-text-xs tw-text-gray-500">
                                    {{ $t('forms.company.users-help', 'Select users associated with this company') }}
                                </p>
                            </div>
                            
                            <!-- Actual FormMultiSelect -->
                            <FormMultiSelect
                                v-else
                                v-model="form.users"
                                :options="usersOptions"
                                :label="$t('forms.company.users', 'Users')"
                                :placeholder="$t('forms.company.select-users', 'Select users')"
                                left-icon="solar:users-group-rounded-bold-duotone"
                                :error="errors.users"
                                :help="$t('forms.company.users-help', 'Select users associated with this company')"
                            />
                        </div>

                        <!-- Categories -->
                        <div class="lg:tw-col-span-2">
                            <!-- Skeleton Loader for Categories -->
                            <div v-if="categoriesLoading" class="tw-space-y-3">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    {{ $t('forms.company.categories', 'Categories') }}
                                </label>
                                <div class="tw-relative">
                                    <!-- Skeleton Button -->
                                    <div class="tw-w-full tw-h-[40px] tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-flex tw-items-center tw-justify-between tw-px-4">
                                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                                            <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-h-4 tw-bg-gray-300 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                        </div>
                                        <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                                <p class="tw-text-xs tw-text-gray-500">
                                    {{ $t('forms.company.categories-help', 'Select categories for this company') }}
                                </p>
                            </div>
                            
                            <!-- Actual FormMultiSelect -->
                            <FormMultiSelect
                                v-else
                                v-model="form.categories"
                                :options="categoriesOptions"
                                :label="$t('forms.company.categories', 'Categories')"
                                :placeholder="$t('forms.company.select-categories', 'Select categories')"
                                left-icon="solar:widget-bold-duotone"
                                :error="errors.categories"
                                :help="$t('forms.company.categories-help', 'Select categories for this company')"
                            />
                        </div>

                        <!-- Certificates -->
                        <div class="lg:tw-col-span-2 tw-mb-6">
                            <!-- Skeleton Loader for Certificates -->
                            <div v-if="certificatesLoading" class="tw-space-y-3">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                                    {{ $t('forms.company.certificates', 'Certificates') }}
                                </label>
                                <div class="tw-relative">
                                    <!-- Skeleton Button -->
                                    <div class="tw-w-full tw-h-[40px] tw-bg-gray-200 tw-rounded-md tw-animate-pulse tw-flex tw-items-center tw-justify-between tw-px-4">
                                        <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                                            <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                            <div class="tw-h-4 tw-bg-gray-300 tw-rounded tw-w-48 tw-animate-pulse"></div>
                                        </div>
                                        <div class="tw-w-5 tw-h-5 tw-bg-gray-300 tw-rounded tw-animate-pulse"></div>
                                    </div>
                                </div>
                                <p class="tw-text-xs tw-text-gray-500">
                                    {{ $t('forms.company.certificates-help', 'Select certificates for this company') }}
                                </p>
                            </div>
                            
                            <!-- Actual FormMultiSelect -->
                            <FormMultiSelect
                                v-else
                                v-model="form.certificates"
                                :options="certificatesOptions"
                                :label="$t('forms.company.certificates', 'Certificates')"
                                :placeholder="$t('forms.company.select-certificates', 'Select certificates')"
                                left-icon="solar:medal-ribbons-star-bold-duotone"
                                :error="errors.certificates"
                                :help="$t('forms.company.certificates-help', 'Select certificates for this company')"
                            />
                        </div>

                        <!-- Social Media Section -->
                        <div class="lg:tw-col-span-2">
                            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">{{ $t('forms.company.social-media', 'Social Media') }}</h3>
                            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                <!-- Facebook -->
                                <FormField
                                    v-model="form.facebook"
                                    type="url"
                                    :label="$t('forms.company.facebook', 'Facebook')"
                                    :placeholder="$t('forms.company.facebook-placeholder', 'Facebook URL')"
                                    left-icon="ri:facebook-fill"
                                    :error="errors.facebook"
                                />

                                <!-- Twitter -->
                                <FormField
                                    v-model="form.twitter"
                                    type="url"
                                    :label="$t('forms.company.x', 'X')"
                                    :placeholder="$t('forms.company.x-placeholder', 'X URL')"
                                    left-icon="hugeicons:new-twitter"
                                    :error="errors.twitter"
                                />

                                <!-- Instagram -->
                                <FormField
                                    v-model="form.instagram"
                                    type="url"
                                    :label="$t('forms.company.instagram', 'Instagram')"
                                    :placeholder="$t('forms.company.instagram-placeholder', 'Instagram URL')"
                                    left-icon="proicons:instagram"
                                    :error="errors.instagram"
                                />

                                <!-- LinkedIn -->
                                <FormField
                                    v-model="form.linkedin"
                                    type="url"
                                    :label="$t('forms.company.linkedin', 'LinkedIn')"
                                    :placeholder="$t('forms.company.linkedin-placeholder', 'LinkedIn URL')"
                                    left-icon="ri:linkedin-fill"
                                    :error="errors.linkedin"
                                />

                                <!-- YouTube -->
                                <FormField
                                    v-model="form.youtube"
                                    type="url"
                                    :label="$t('forms.company.youtube', 'YouTube')"
                                    :placeholder="$t('forms.company.youtube-placeholder', 'YouTube URL')"
                                    left-icon="famicons:logo-youtube"
                                    :error="errors.youtube"
                                />
                            </div>
                        </div>

                        
                    </div>

                    <!-- Form Actions -->
                    <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-200">
                        <a-button 
                            type="button" 
                            intent="simple" 
                            @click="handleCancel"
                            :disabled="isSubmitting"
                        >
                            {{ $t('buttons.cancel') }}
                        </a-button>
                        <a-button 
                            type="submit" 
                            intent="primary" 
                            :loading="isSubmitting"
                            :disabled="isSubmitting"
                        >
                            {{ $t('buttons.create') }}
                        </a-button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormFileUpload from '@/components/form/FormFileUpload.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import useAlert from '@/composables/useAlert';
import { forms } from '@/api/app';
import companyApi from '@/api/companies';

const router = useRouter();
const { t } = useI18n();
// Form data
const form = ref({
    name: '',
    streaming_platform: '',
    logo: null,
    background_image: null,
    address: '',
    phone: '',
    users: [],
    categories: [],
    certificates: [],
    primary_email: '',
    secondary_email: '',
    website: '',
    country_id: '',
    primary_phone: '',
    secondary_phone: '',
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    youtube: ''
});

// Form state
const isSubmitting = ref(false);
const errors = ref({});

// Loading states for form data
const categoriesLoading = ref(false);
const certificatesLoading = ref(false);
const usersLoading = ref(false);

// Streaming platform options
const streamingPlatforms = [
    { id: 'youtube', name: 'YouTube' },
    { id: 'twitch', name: 'Twitch' },
    { id: 'facebook', name: 'Facebook Live' },
    { id: 'instagram', name: 'Instagram Live' },
    { id: 'tiktok', name: 'TikTok Live' },
    { id: 'linkedin', name: 'LinkedIn Live' },
    { id: 'other', name: 'Other' }
];

const usersOptions = ref([]);

const categoriesOptions = ref([]);

const certificatesOptions = ref([]);

const countriesOptions = ref([]);

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.value.name.trim()) {
        errors.value.name = t('errors.required', { field: t('fields.name') });
    }

    if (!form.value.primary_email.trim()) {
        errors.value.primary_email = t('errors.required', { field: t('forms.company.primary-email') });
    } else if (!isValidEmail(form.value.primary_email)) {
        errors.value.primary_email = t('errors.email');
    }

    if (form.value.secondary_email && !isValidEmail(form.value.secondary_email)) {
        errors.value.secondary_email = t('errors.email');
    }

    // if (!form.streaming_platform) {
    //     errors.streaming_platform = t('errors.required', { field: t('forms.company.streaming-platform') });
    // }

    if (form.value.primary_phone && !isValidPhone(form.value.primary_phone) && !!undefined) {
        errors.value.primary_phone = t('errors.phone');
    }

    if (form.value.secondary_phone && !isValidPhone(form.value.secondary_phone) && !!undefined) {
        errors.value.secondary_phone = t('errors.phone');
    }

    if (form.value.website && !isValidUrl(form.value.website)) {
        errors.value.website = t('errors.url');
    }

    if (form.value.facebook && !isValidUrl(form.value.facebook)) {
        errors.value.facebook = t('errors.url');
    }

    if (form.value.twitter && !isValidUrl(form.value.twitter)) {
        errors.value.twitter = t('errors.url');
    }

    if (form.value.instagram && !isValidUrl(form.value.instagram)) {
        errors.value.instagram = t('errors.url');
    }

    if (form.value.linkedin && !isValidUrl(form.value.linkedin)) {
        errors.value.linkedin = t('errors.url');
    }

    if (form.value.youtube && !isValidUrl(form.value.youtube)) {
        errors.value.youtube = t('errors.url');
    }

    console.log('Form errors:', errors.value);
    return Object.keys(errors.value).length === 0;
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

const isValidPhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

// Form submission
const handleSubmit = async () => {
    console.log('Form is submitted');
    if (!validateForm()) {
        console.log('Form is not valid');
        return;
    }

    isSubmitting.value = true;

    try {
        // Create FormData for file uploads
        const formData = new FormData();
        
        // Add text fields
        formData.append('name', form.value.name);
        formData.append('primary_email', form.value.primary_email);
        formData.append('secondary_email', form.value.secondary_email || '');
        formData.append('website', form.value.website || '');
        formData.append('streaming_platform', form.value.streaming_platform);
        formData.append('country_id', form.value.country_id || '');
        formData.append('primary_phone', form.value.primary_phone || '');
        formData.append('secondary_phone', form.value.secondary_phone || '');
        formData.append('address', form.value.address || '');
        formData.append('description', form.value.description || '');
        formData.append('facebook', form.value.facebook || '');
        formData.append('twitter', form.value.twitter || '');
        formData.append('instagram', form.value.instagram || '');
        formData.append('linkedin', form.value.linkedin || '');
        formData.append('youtube', form.value.youtube || '');

        // Always send relationship ID arrays (even when empty) so the backend always receives these keys
        const userIds = form.value.users.map(user => user.id);
        formData.append('user_ids', JSON.stringify(userIds));

        const categoryIds = form.value.categories.map(category => category.id);
        formData.append('category_ids', JSON.stringify(categoryIds));

        const certificateIds = form.value.certificates.map(certificate => certificate.id);
        formData.append('certificate_ids', JSON.stringify(certificateIds));

        // Add files if selected
        if (form.value.logo instanceof File) {
            formData.append('logo', form.value.logo);
        }
        if (form.value.background_image instanceof File) {
            formData.append('background_image', form.value.background_image);
        }

        console.log('Form data:', formData);

        // TODO: Replace with actual API call
        // const response = await createCompany(formData);

        const response = await companyApi.admin.create(formData);
        console.log('Response:', response);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 2000));

        useAlert(t('messages.created-successfully', { field: t('fields.name') }), 'success');
        router.push({ name: 'pages.admin.companies' });

    } catch (error) {
        console.error('Error creating company:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors.value[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-wrong-happened'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const handleCancel = () => {
    router.push({ name: 'pages.admin.companies' });
};

const getFormData = async () => {
    // Set all loading states to true at the same time
    usersLoading.value = true;
    categoriesLoading.value = true;
    certificatesLoading.value = true;

    // Create individual promises for each API call with their own loading state management
    const usersPromise = forms.users(['exhibitor'])
        .then(response => {
            usersOptions.value = response.data || [];
            return response;
        })
        .catch(error => {
            console.error('Error fetching users:', error);
            useAlert(t('errors.failed-to-load-users', 'Failed to load users'), 'warning');
            usersOptions.value = [];
        })
        .finally(() => {
            usersLoading.value = false;
        });

    const categoriesPromise = forms.categories('company')
        .then(response => {
            categoriesOptions.value = response.data || [];
            return response;
        })
        .catch(error => {
            console.error('Error fetching categories:', error);
            useAlert(t('errors.failed-to-load-categories', 'Failed to load categories'), 'warning');
            categoriesOptions.value = [];
        })
        .finally(() => {
            categoriesLoading.value = false;
        });

    const certificatesPromise = forms.certificates('company')
        .then(response => {
            certificatesOptions.value = response.data || [];
            return response;
        })
        .catch(error => {
            console.error('Error fetching certificates:', error);
            useAlert(t('errors.failed-to-load-certificates', 'Failed to load certificates'), 'warning');
            certificatesOptions.value = [];
        })
        .finally(() => {
            certificatesLoading.value = false;
        });

    const countriesPromise = forms.countries()
        .then(response => {
            countriesOptions.value = response.data || [];
            return response;
        })
        .catch(error => {
            console.error('Error fetching countries:', error);
            useAlert(t('errors.failed-to-load-countries', 'Failed to load countries'), 'warning');
            countriesOptions.value = [];
        });

    // Wait for all promises to complete (but each manages its own loading state)
    try {
        await Promise.all([usersPromise, categoriesPromise, certificatesPromise, countriesPromise]);
    } catch (error) {
        console.error('Error in form data loading:', error);
    }
}

onMounted(async () => {
    await getFormData();
})
</script>

<style scoped>
/* Custom styles for the form */
.form-section {
    @apply tw-space-y-6;
}

.form-section-title {
    @apply tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4;
}
</style>